import { ReactElement } from 'react';
import { Callout, CalloutText, CalloutProps } from '@twilio-paste/core/callout';
import { types } from '../definitions';

/**
 * Maps an element's className to its corresponding Paste component
 *  - e.g. className `notification notification-warning` will render <Callout variant="warning" />
 *  - Return type has "false" explicitly included to adhere how `html-react-parser` works.
 *    Refer to the notes in '/renderPasteComponentByHtmlTag.tsx` for more details.
 *  - Disclaimer: At the time of writing, our only use case is handling classNames set by ZendDesk.
 *    This is not a generic utility function that can handle classNames provided by another party
 */
export const renderPasteComponentByClassName = ({
  attributes,
  childElements,
}: types.RenderPasteComponentByClassNameParams): ReactElement | false => {
  const className = attributes?.class;

  if (!className) {
    return false;
  }

  const pasteComponentsByClassName: {
    [className: string]: () => ReactElement;
  } = {
    // This may not be necessary - waiting on stakeholders to make decision
    notification: (): ReactElement => {
      const classNames = className.split(' ');
      const notificationVariants = {
        'notification-alert': 'warning',
        'notification-success': 'success',
        'notification-error': 'error',
        default: 'neutral',
      };

      let variant = notificationVariants.default as CalloutProps['variant'];

      classNames.forEach((name) => {
        if (name in notificationVariants) {
          variant = notificationVariants[name];
        }
      });
      return (
        <Callout variant={variant} marginY="space50">
          <CalloutText>{childElements}</CalloutText>
        </Callout>
      );
    },
    // Add more classNames and their corresponding components here
  };

  for (const key in pasteComponentsByClassName) {
    if (className.includes(key)) {
      return pasteComponentsByClassName[key]();
    }
  }

  return false;
};
